import t from "@/lang/lang";
import {
  useListPosition,
  usePutPosition,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-work-order-positions-offline";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { Fragment, useMemo, useState } from "react";
import { NullUUID, UUID } from "@bitory-gmbh/nidavellir";
import {
  useGetAirHandlingUnit,
  useListAirHandlingUnit,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-air-handling-unit-offline";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellCheckbox,
  ETableCellInput,
  ETableCellSelect,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { useGetWorkOrder } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-work-order-offline";
import { useFindOrCreateAhuData } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/use-resolve-ahu-data";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { hasFieldError } from "@/shared/lib/errors/validation-error";
import { InspectionSheet } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/inspection-sheet";
import { DeletePositionDialog } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/delete-position-dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { DialogDescription } from "@radix-ui/react-dialog";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import {
  Position,
  updateAhu,
  updateDescription,
  updateUnitCount,
  updateUnitHours,
  updateUnitPrice,
  updateMicrobialInspectionRequired,
  updatePhotoDocumentationRequired,
  updateStructuralInspectionRequired,
  updateVisualInspectionRequired,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";

/*
 * WorkOrderPositionsTable renders the positions of
 * a work order. The Table is dependent on being
 * within a htz work order context.
 */
export function WorkOrderPositionsTable({
  workOrderId,
}: {
  workOrderId: string;
}) {
  const { data: positions, isLoading, error } = useListPosition(workOrderId);

  return (
    <ETable>
      <ETableHeader>
        <ETableRow>
          <ETableHead>{t("Pos")}</ETableHead>
          <ETableHead>{t("Erledigt")}</ETableHead>
          <ETableHead>{t("RLT-Anlage")}</ETableHead>
          <ETableHead>{t("Titel")}</ETableHead>
          <ETableHead>{t("Optional?")}</ETableHead>
          <ETableHead colSpan={3}>{t("Aktionen")}</ETableHead>
        </ETableRow>
      </ETableHeader>
      <ETableBody>
        {isLoading && <TableLoadingRow colSpan={8} />}
        {error !== undefined && <TableRtkErrorRow colSpan={8} error={error} />}
        {positions && positions.length === 0 && <TableEmptyRow colSpan={8} />}
        {positions && positions.length !== 0 && (
          <SplitPositionsByAhu
            // Remount only when the *set* of positions changes (e.g. after
            // copying an offer into the work order), so rows re-initialize their
            // local state from the new data. Keying on workOrder.updatedAt would
            // also remount on every routine field save, which would tear down an
            // open inspection sheet mid-edit.
            key={positions.map((position) => position.id).join(",")}
            positions={positions}
          />
        )}
      </ETableBody>
    </ETable>
  );
}

function SplitPositionsByAhu({ positions }: { positions: Position[] }) {
  // We collect the ahu ids in the positions to be able
  // to render the positions grouped by ahu id.
  const ahuIds = useMemo(
    () =>
      positions.reduce((prev, curr) => {
        // Positions without a ahu id are
        // grouped by a null UUID.
        let ahuId: UUID;
        if (!curr.ahuId) {
          ahuId = NullUUID;
        } else {
          ahuId = curr.ahuId;
        }

        // Only add the id if it does not exist
        if (prev.some((p) => p === ahuId)) {
          return prev;
        }

        return [...prev, ahuId];
      }, [] as UUID[]),
    [positions],
  );

  return (
    <>
      {ahuIds.map((ahuId) => (
        <Fragment key={ahuId}>
          <ETableRow>
            <ETableCell colSpan={8} className="bg-muted h-8 py-0 font-medium">
              {ahuId === NullUUID ? (
                t("Anlagenübergreifend")
              ) : (
                <AhuName ahuId={ahuId} />
              )}
            </ETableCell>
          </ETableRow>
          {positions?.filter(byAhuId(ahuId)).map((position) => (
            <PositionTableRow key={position.id} position={position} />
          ))}
        </Fragment>
      ))}
    </>
  );
}

function AhuName({ ahuId }: { ahuId: string }) {
  const { data: airHandlingUnits } = useGetAirHandlingUnit(ahuId);

  return <span>{airHandlingUnits?.name}</span>;
}

function byAhuId(ahuId: string) {
  return (a: Position) => {
    if (!a.ahuId) {
      return NullUUID === ahuId;
    }
    return a.ahuId === ahuId;
  };
}

function PositionTableRow({ position }: { position: Position }) {
  const { data: workOrder } = useGetWorkOrder(position.workOrderId);
  const [request, setRequest] = useState(position);
  const mutation = usePutPosition(position.workOrderId);
  const [, { isLoading, isSuccess, error, reset }] = mutation;
  useDebouncedMutation(request, setRequest, mutation, position, {
    // Users will typically only select the checkbox
    // or selct the AHU. The longer default debounce
    // is a bit awkward in these cases.
    // If it appears that users actually update the
    // position title regularly, consider splitting
    // the component and use separate debounce hooks.
    delay: 0,
    resetOnError: false,
    toastError: true,
  });

  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();

  // Will become available eventually.
  if (!workOrder) return null;

  const disabled = workOrder.immutable;

  return (
    <ETableRow>
      <ETableCell className="text-center">{position.number}</ETableCell>
      <ETableCellCheckbox
        checked={request.completed}
        onCheckedChange={() =>
          setRequest({ ...request, completed: !request.completed })
        }
        disabled={isLoading || disabled}
        label={request.completed ? t("ja") : t("nein")}
      />
      <AhuSelectCell
        position={position}
        workOrder={workOrder!}
        onPositionChange={setRequest}
      />
      <ETableCellInput
        value={request.title}
        onChange={(e) => {
          setRequest({ ...request, title: e.target.value });
          resetNotReadyErrorField(`position.${position.id}.title`);
        }}
        disabled={disabled}
        hasError={hasFieldError(notReadyError, `position.${position.id}.title`)}
      />
      <ETableCellCheckbox
        checked={position.optional}
        label={position.optional ? t("ja") : t("nein")}
        disabled
      />
      <ETableCell className="w-20">
        <DetailsDialog position={request} onPositionChange={setRequest} />
      </ETableCell>
      <ETableCell className="w-24">
        <InspectionSheet
          workOrder={workOrder!}
          position={request}
          // Inspection edits update the row's local `request`; the row's own
          // useDebouncedMutation above persists them. The status object carries
          // that save's state so the inspection tabs can reflect saving/errors.
          onPositionChange={[
            setRequest,
            { isLoading, error, reset, isSuccess },
          ]}
          // checking for processId === null disables inspection when
          // the work order is only part of a commitment and not in a
          // process. Should this not be sufficient anymore a more
          // (need for inspection outside of process) this should be
          // disabled with a flag.
          disabled={disabled || workOrder.processId === null}
        />
      </ETableCell>
      <ETableCell className="w-8">
        <DeletePositionDialog workOrder={workOrder!} position={position} />
      </ETableCell>
    </ETableRow>
  );
}

function AhuSelectCell({
  position,
  onPositionChange,
  workOrder,
}: {
  position: Position;
  onPositionChange: (position: Position) => void;
  workOrder: WorkOrder;
}) {
  const { data: airHandlingUnits } = useListAirHandlingUnit(
    workOrder.id,
    workOrder.customerId,
  );
  const items = [
    ...(airHandlingUnits ?? []).map((ahu) => ({
      label: ahu.name,
      value: ahu.id,
    })),
    { label: t("Anlagenübergreifend"), value: NullUUID },
  ];

  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();
  const errorKey = `position.${position.id}.airHandlingUnitId`;
  const hasError = hasFieldError(notReadyError, errorKey);

  const { findOrCreateAhuData, isLoading: isResolving } =
    useFindOrCreateAhuData(
      workOrder.id,
      workOrder.customerId,
      workOrder.serviceObjectId,
    );

  const setAirHandlingUnit = async (ahuId: string | null) => {
    if (ahuId === NullUUID || ahuId === null) {
      onPositionChange(updateAhu(position, null, null));
      resetNotReadyErrorField(errorKey);
      return;
    }

    try {
      const ahuDataId = await findOrCreateAhuData(ahuId);
      onPositionChange(updateAhu(position, ahuId, ahuDataId));
      resetNotReadyErrorField(errorKey);
    } catch {
      // Error state is managed by useResolveAhuData
    }
  };

  return (
    <ETableCellSelect
      label={t("RLT-Anlagen")}
      items={items}
      value={position.ahuId ?? ""}
      onValueChange={setAirHandlingUnit}
      disabled={isResolving || workOrder.immutable}
      hasError={hasError}
    />
  );
}

function DetailsDialog({
  position,
  onPositionChange,
  disabled,
}: {
  position: Position;
  onPositionChange: (position: Position) => void;
  disabled?: boolean;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost">
          {t("Details")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Details")}</DialogTitle>
          <DialogDescription>{t("Details zur Position")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <Textarea
            value={position.description}
            onChange={(e) =>
              onPositionChange(updateDescription(position, e.target.value))
            }
            disabled={disabled}
          />
          <div className="grid grid-cols-3 gap-4">
            <div className="grid gap-1.5">
              <Label>{t("Anzahl")}</Label>
              <Input
                type="number"
                value={position.unitCount}
                onChange={(e) =>
                  onPositionChange(
                    updateUnitCount(
                      position,
                      parseInt(e.target.value, 10) || 0,
                    ),
                  )
                }
                disabled={disabled}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("Einzelpreis")}</Label>
              <Input
                type="number"
                value={position.unitPrice}
                onChange={(e) =>
                  onPositionChange(
                    updateUnitPrice(position, Number(e.target.value)),
                  )
                }
                disabled={disabled}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("Gesamtpreis")}</Label>
              <div className="flex h-9 items-center rounded-md border px-3 text-sm">
                {position.totalPrice.toLocaleString("de", {
                  minimumFractionDigits: 2,
                })}{" "}
                &euro;
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>{t("Stunden pro Einheit")}</Label>
              <Input
                type="number"
                step="0.25"
                value={position.unitHours}
                onChange={(e) =>
                  onPositionChange(
                    updateUnitHours(position, Number(e.target.value)),
                  )
                }
                disabled={disabled}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("Gesamtstunden")}</Label>
              <div className="flex h-9 items-center rounded-md border px-3 text-sm">
                {position.totalHours.toLocaleString("de", {
                  minimumFractionDigits: 2,
                })}
              </div>
            </div>
          </div>
          <div className="grid gap-2">
            <InspectionRequiredCheckbox
              label={t("Konstruktive Beurteilung nötig")}
              checked={position.structuralInspectionRequired}
              onCheckedChange={(value) =>
                onPositionChange(
                  updateStructuralInspectionRequired(position, value),
                )
              }
              disabled={disabled}
            />
            <InspectionRequiredCheckbox
              label={t("Visuelle Beurteilung nötig")}
              checked={position.visualInspectionRequired}
              onCheckedChange={(value) =>
                onPositionChange(
                  updateVisualInspectionRequired(position, value),
                )
              }
              disabled={disabled}
            />
            <InspectionRequiredCheckbox
              label={t("Mikrobiologische Inspektion nötig")}
              checked={position.microbialInspectionRequired}
              onCheckedChange={(value) =>
                onPositionChange(
                  updateMicrobialInspectionRequired(position, value),
                )
              }
              disabled={disabled}
            />
            <InspectionRequiredCheckbox
              label={t("Fotodokumentation nötig")}
              checked={position.photoDocumentationRequired}
              onCheckedChange={(value) =>
                onPositionChange(
                  updatePhotoDocumentationRequired(position, value),
                )
              }
              disabled={disabled}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface InspectionRequiredCheckboxProps {
  label: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  disabled?: boolean;
}

export function InspectionRequiredCheckbox({
  label,
  checked,
  onCheckedChange,
  disabled,
}: InspectionRequiredCheckboxProps) {
  return (
    <div className="flex items-center space-x-2">
      <Checkbox
        id={label}
        checked={checked}
        onCheckedChange={(state) => {
          switch (state) {
            case true:
            case false:
              onCheckedChange(state);
              break;
            default:
          }
        }}
        disabled={disabled}
      />
      <label
        htmlFor={label}
        className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        {label}
      </label>
    </div>
  );
}
